import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const FAQS = [
  {
    q: "How much will my website cost?",
    a: "Most projects fit one of our packages — CAD $349, $599, or $849 — and custom projects start at $1,000. Every business is different, so we're glad to adjust scope and pricing around your budget, and we always agree on the total together before any work begins. Your domain, hosting, and any paid extras are separate. Payment is simple: 50% up front, and 50% just before launch.",
  },
  {
    q: "How long will it take?",
    a: "We'll confirm your timeline before we start, based on the pages and features we agree on together. The biggest help is on your side — sending your content, account access, and feedback promptly keeps everything humming along on schedule.",
  },
  {
    q: "Will I own my website?",
    a: "Yes — completely. After final payment, the finished site is yours, with full access to manage it. Your domain and business accounts stay in your name, where they belong. Third-party tools follow their own licences, and we'll confirm platform editing and transfer options with you before we start. Ongoing support is always optional — but always here if you want it.",
  },
];

const Faq = () => {
  const [open, setOpen] = useState(0);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => document.getElementById("faq-jsonld")?.remove();
  }, []);

  return (
    <section className="py-12 sm:py-16 lg:py-20" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <Eyebrow>Common Questions</Eyebrow>
          <h2 className="font-head font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#233D35] mt-3 leading-[0.95]">
            Fair questions, straight answers.
          </h2>
        </Reveal>

        <div className="mt-10 border-t border-[#E2DACB]">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.08}>
                <div className="border-b border-[#E2DACB] bg-white/40" data-testid={`faq-item-${i}`}>
                  <button
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${i}`}
                    data-testid={`faq-question-${i}`}
                    className="w-full flex items-center justify-between gap-4 text-left px-4 sm:px-6 py-5 group"
                  >
                    <span className={`font-head font-bold uppercase tracking-tight text-lg sm:text-xl transition-colors duration-200 ${
                      isOpen ? "text-[#C58A47]" : "text-[#233D35] group-hover:text-[#C58A47]"
                    }`}>
                      {f.q}
                    </span>
                    <Plus
                      size={22}
                      className={`shrink-0 text-[#C58A47] transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
                      aria-hidden="true"
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="answer"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p id={`faq-answer-${i}`} className="px-4 sm:px-6 pb-6 text-[#77786F] text-base leading-relaxed max-w-3xl" data-testid={`faq-answer-${i}`}>
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal className="mt-8 text-center">
          <p className="text-sm text-[#77786F]">
            Still wondering about something?{" "}
            <a href="mailto:goodworkweb1@gmail.com" className="link-rule font-semibold text-[#233D35]" data-testid="faq-email-link">
              Ask us directly
            </a>{" "}
            — we answer like neighbours, not salespeople.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Faq;
